import React from 'react';
import { theme } from '../../styles/design-system';

const Card = ({
  children,
  variant = 'default',
  padding = 'md',
  className = '',
  onClick,
  hover = false,
  ...props
}) => {
  const baseStyles = {
    backgroundColor: 'var(--card-bg)',
    borderRadius: theme.borderRadius.xl,
    border: '1px solid var(--card-border)',
    boxShadow: theme.shadows.sm,
    transition: 'all 0.2s ease-in-out',
    position: 'relative',
    overflow: 'hidden',
  };

  const variantStyles = {
    default: {
      backgroundColor: 'var(--card-bg)',
      border: '1px solid var(--card-border)',
    },
    elevated: {
      backgroundColor: 'var(--card-bg)',
      border: 'none',
      boxShadow: theme.shadows.lg,
    },
    outlined: {
      backgroundColor: 'transparent',
      border: '2px solid var(--card-border)',
      boxShadow: 'none',
    },
    glass: {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(10px)',
      boxShadow: 'none',
    },
  };

  const paddingStyles = {
    none: { padding: 0 },
    sm: { padding: theme.spacing[3] },
    md: { padding: theme.spacing[4] },
    lg: { padding: theme.spacing[6] },
    xl: { padding: theme.spacing[8] },
  };

  const hoverStyles = hover ? {
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows.lg,
      cursor: onClick ? 'pointer' : 'default',
    },
  } : {};

  const combinedStyles = {
    ...baseStyles,
    ...variantStyles[variant],
    ...paddingStyles[padding],
    ...hoverStyles,
  };

  const { style: customStyle, ...otherProps } = props;

  return (
    <div
      className={className}
      style={{ ...combinedStyles, ...customStyle }}
      onClick={onClick}
      {...otherProps}
    >
      {children}
    </div>
  );
};

// Card sub-components
export const CardHeader = ({ children, className = '', ...props }) => (
  <div
    className={className}
    style={{
      paddingBottom: theme.spacing[4],
      borderBottom: '1px solid var(--card-border)',
      marginBottom: theme.spacing[4],
    }}
    {...props}
  >
    {children}
  </div>
);

export const CardTitle = ({ children, className = '', ...props }) => (
  <h3
    className={className}
    style={{
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.semibold,
      color: 'var(--text-primary)',
      margin: 0,
    }}
    {...props}
  >
    {children}
  </h3>
);

export const CardDescription = ({ children, className = '', ...props }) => (
  <p
    className={className}
    style={{
      fontSize: theme.typography.fontSize.sm,
      color: 'var(--text-secondary)',
      margin: 0,
      marginTop: theme.spacing[1],
    }}
    {...props}
  >
    {children}
  </p>
);

export const CardContent = ({ children, className = '', ...props }) => (
  <div
    className={className}
    style={{
      color: 'var(--text-primary)',
    }}
    {...props}
  >
    {children}
  </div>
);

export const CardFooter = ({ children, className = '', ...props }) => (
  <div
    className={className}
    style={{
      paddingTop: theme.spacing[4],
      borderTop: '1px solid var(--card-border)',
      marginTop: theme.spacing[4],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
    {...props}
  >
    {children}
  </div>
);

export default Card;
